import { WeekAvailability } from "./AvailabilityChart";
import { WorkshopWorker, WorkshopJob } from "../../types/garage/atelier";
import { AvailabilityBlock } from "@store/disponibiliteStore";
import { getMonday, toISO } from "@store/disponibiliteStore";

const DEFAULT_WORK_HOURS = { start: 8, end: 18 };

/** Get Mon–Fri dates for a week starting from a given Monday */
function getWorkWeekDates(monday: Date): string[] {
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return toISO(d);
  });
}

/** Get the day-of-week index (0=Mon … 6=Sun) for a date string */
function dayOfWeekIndex(dateStr: string): number {
  const d = new Date(dateStr + "T00:00:00");
  const jsDay = d.getDay(); // 0=Sun
  return jsDay === 0 ? 6 : jsDay - 1;
}

/** Compute unavailability hours for a worker on a specific date */
function getBlockedHours(
  blocks: AvailabilityBlock[],
  workerId: string,
  dateStr: string,
): number {
  const dow = dayOfWeekIndex(dateStr);
  let blocked = 0;
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    const matches =
      b.date === dateStr ||
      (b.recurring && b.dayOfWeek === dow);
    if (matches) blocked += b.durationHours;
  }
  return blocked;
}

/** Format a date range string dd/mm – dd/mm */
function formatRange(dates: string[]): string {
  const fmt = (d: string) => {
    const [, m, day] = d.split("-");
    return `${day}/${m}`;
  };
  return `${fmt(dates[0])} - ${fmt(dates[dates.length - 1])}`;
}

export function computeWeeklyAvailability(
  selectedDate: string,
  workers: WorkshopWorker[],
  jobs: WorkshopJob[],
  blocks: AvailabilityBlock[],
  workHours?: { start: number; end: number },
): WeekAvailability[] {
  const hours = workHours ?? DEFAULT_WORK_HOURS;
  const dailyCapacity = hours.end - hours.start;

  const monday = getMonday(selectedDate);
  const weeks: WeekAvailability[] = [];

  for (let w = 0; w < 3; w++) {
    const weekMonday = new Date(monday);
    weekMonday.setDate(monday.getDate() + w * 7);
    const dates = getWorkWeekDates(weekMonday);

    // Compute total capacity for this week
    let totalCapacityHours = 0;
    for (const worker of workers) {
      for (const date of dates) {
        const blocked = getBlockedHours(blocks, worker.id, date);
        totalCapacityHours += Math.max(0, dailyCapacity - blocked);
      }
    }

    // Compute total occupied hours from atelier jobs
    const occupiedHours = jobs
      .filter((j) => dates.includes(j.date))
      .reduce((sum, j) => sum + j.durationHours, 0);

    const freePct =
      totalCapacityHours > 0
        ? Math.max(0, Math.round(((totalCapacityHours - occupiedHours) / totalCapacityHours) * 100))
        : 0;

    // Single bar showing available atelier capacity
    weeks.push({
      dateRange: formatRange(dates),
      bars: [{ color: "#6366F1", height: freePct }],
    });
  }

  return weeks;
}
